import type { Scene } from '@babylonjs/core/scene';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Material } from '@babylonjs/core/Materials/material';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import '@babylonjs/core/Particles/particleSystemComponent';
import type { DozerConfig } from '../config/dozer.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { laneCenterX } from '../level/segments/segmentBuilders';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { TrackPresenter } from '../level/TrackPresenter';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';
import type { ObstaclePresenter } from '../obstacles/ObstaclePresenter';
import { createDustSprite } from '../rendering/DustEffect';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { clamp, clamp01, damp, DEG_TO_RAD, lerp, smoothstep, TAU, type Vec3 } from '../utils/math';
import type { DozerManager } from './DozerManager';
import { DOZER_RIG, DOZER_SIDES, type DozerSide } from './dozerRig';
import { DozerPhase, type DozerRide } from './DozerRide';

/** ParticleSystem.manualEmitCount value that means "emit by emitRate" (see DustEffect). */
const RATE_EMISSION = -1;
/** Texture slots of the belt material that follow the belt UVs (PBR from the GLB, the greybox pattern). */
const BELT_TEXTURE_SLOTS = ['albedoTexture', 'bumpTexture', 'metallicTexture', 'ambientTexture', 'reflectivityTexture', 'microSurfaceTexture'] as const;
/** Lateral speeds below this forward speed (m/s) do not turn the body – the parked dozer never swings. */
const MIN_STEER_FORWARD = 1;
/** Zero scale is degenerate – a pile or mound that is not there yet is hidden instead. */
const MIN_SCALE = 0.01;
/** Exponential growth of the pile: this many time constants fit into DozerConfig.presentation.pile.growSeconds. */
const GROW_TIME_CONSTANTS = 3;

/** World pose of the seat socket (rider's hips, the root of ANIM_Drive) of the driven dozer – PlayerView puts the rider there. */
export interface SeatPose {
  readonly position: Vector3;
  /** Standing in front of the seat (model without ANIM_Drive, DozerConfig.presentation.rider.fallbackOffset). */
  readonly fallback: Vector3;
  /** World heading of the machine (radians, like TrackFrame.yaw) – the rider looks along it. */
  yaw: number;
}

/** A rig node with its rest transform (restored before the pooled visual is reused). */
interface RigNode {
  readonly node: TransformNode;
  readonly position: Vector3;
  readonly rotation: Quaternion | null;
}

/** Rest geometry of one dozer visual in vehicle space (x right, y up, z forward; mirrored GLB nodes resolved). */
interface RestGeometry {
  readonly pivot: Vector3;
  readonly bladeTip: Vector3;
  readonly exhaust: Vector3;
  /** Body-side eye (barrel origin, vehicle space) and blade-side eye (rod origin, vehicle space at rest) per side. */
  readonly bodyEye: Readonly<Record<DozerSide, Vector3>>;
  readonly bladeEye: Readonly<Record<DozerSide, Vector3>>;
}

interface Bottle {
  readonly visual: EntityVisual;
  readonly from: Vector3;
  readonly start: number;
  /** −0.5 … 0.5: where in the fan the bottle flies. */
  readonly fan: number;
}

interface Entry {
  readonly ride: DozerRide;
  readonly visual: EntityVisual;
  readonly nodes: Map<string, RigNode>;
  readonly wheels: { readonly rig: RigNode; readonly radius: number }[];
  /** Textures of the belt material (shared by every copy – the moving dozer sets the offset). */
  readonly belt: Texture[];
  readonly rest: RestGeometry;
  readonly exhaust: ParticleSystem;
  readonly exhaustOrigin: Vector3;
  /** Blade lift (degrees, positive = up), the dip of the last heap (s left), the body's steer yaw (rad). */
  lift: number;
  dipLeft: number;
  steer: number;
  /** Earth pile in front of the blade (segment space) and its size; null until the first heap. */
  pile: EntityVisual | null;
  pileScale: number;
  pileTarget: number;
  /** Render time the pile started to slide into the pit (null: not yet). */
  dumpedAt: number | null;
  /** Earth in the pit: the box flush with the road and a flat mound per lane. */
  fill: Mesh | null;
  mounds: EntityVisual[];
}

/**
 * Visual side of the dozer set piece (DozerManager is the gameplay): the parked dozer's visual (ObstaclePresenter's,
 * OBS_DOZER_A → VEH_DOZER_A) follows the ride – interpolated between fixed steps, turned into lane changes, the engine
 * rumbling – and its rig moves by node name (dozer/dozerRig.ts, GLB and greybox alike): the blade lowers to push, the
 * lift cylinders telescope (barrel and rod aimed at each other), sprockets and rollers turn, the rubber belts scroll.
 * Exhaust smoke at the stack, earth spraying at the blade, the pile growing in front of it with each heap, bottles
 * springing out of a pushed heap into the rider, the pile sliding into the pit and the earth rising there.
 * Presentation only – nothing here affects gameplay.
 */
export class DozerPresenter {
  private readonly entries = new Map<DozerRide, Entry>();
  private readonly spray: ParticleSystem;
  private readonly sprayOrigin = new Vector3();
  private readonly sprite: Texture;
  private readonly fillMaterial: PBRMaterial;
  private readonly bottles: Bottle[] = [];
  private readonly seat: SeatPose = { position: new Vector3(), fallback: new Vector3(), yaw: 0 };
  private readonly scratch = new Vector3();
  private readonly scratch2 = new Vector3();
  private readonly turn = new Quaternion();
  private time = 0;

  constructor(
    private readonly scene: Scene,
    private readonly pool: VisualPool,
    private readonly track: TrackPresenter,
    private readonly obstacleView: ObstaclePresenter,
    private readonly dozers: DozerManager,
    private readonly config: DozerConfig,
    private readonly world: WorldConfig,
    /** Size of the heap visual at scale 1 (OBS_DIRT_MOUND_A collider) – the pile and the mounds are sized by it. */
    private readonly heapSize: Vec3,
    /** Fixed simulation step (s): the lateral speed of the ride between two steps. */
    private readonly fixedStep: number,
    events: EventBus<GameEvents>,
  ) {
    this.sprite = createDustSprite(scene);
    this.spray = this.createSpray();
    this.fillMaterial = new PBRMaterial('MAT_FX_DOZER_FILL', scene);
    this.fillMaterial.albedoColor = Color3.FromHexString(config.presentation.fill.color).toLinearSpace();
    this.fillMaterial.metallic = 0;
    this.fillMaterial.roughness = 1;

    events.on('dozer:heapPushed', ({ ride, heap }) => this.onHeap(ride, heap));
    events.on('dozer:pitReached', ({ ride, filled }) => {
      const entry = this.entries.get(ride);
      if (entry && filled) entry.dumpedAt = this.time;
    });
    events.on('segment:recycled', ({ segment }) => this.dropSegment(segment));
  }

  /** The seat of the driven dozer this frame (after render), or null while nobody drives. */
  seatPose(): Readonly<SeatPose> | null {
    const ride = this.dozers.active;
    const entry = ride ? this.entries.get(ride) : undefined;
    return entry ? this.seat : null;
  }

  /** Per frame, before the player view: poses between the last two fixed steps (`alpha`). */
  render(alpha: number, dt: number, paused: boolean): void {
    const step = paused ? 0 : dt;
    this.time += step;
    const alive = new Set(this.dozers.all());
    for (const [ride, entry] of this.entries) if (!alive.has(ride)) this.release(entry);
    let pushing = false;
    for (const ride of alive) {
      const entry = this.entries.get(ride) ?? this.create(ride);
      if (!entry) continue;
      this.update(entry, alpha, step);
      if (ride === this.dozers.active) {
        this.updateSeat(entry);
        pushing = entry.pile !== null && entry.dumpedAt === null;
      }
    }
    // After a manual burst Babylon leaves manualEmitCount at 0, which disables rate emission – re-enable it.
    if (this.spray.manualEmitCount === 0) this.spray.manualEmitCount = RATE_EMISSION;
    this.spray.emitRate = pushing && !paused ? this.config.presentation.spray.pushRate : 0;
    this.updateBottles();
  }

  /** New run: everything back to the pool (the track's recycle events usually did it already). */
  reset(): void {
    for (const entry of this.entries.values()) this.release(entry);
    for (const bottle of this.bottles.splice(0)) this.pool.release(bottle.visual);
    this.spray.reset();
  }

  // ── Per frame ────────────────────────────────────────────────────────────

  private update(entry: Entry, alpha: number, dt: number): void {
    const { ride } = entry;
    const p = this.config.presentation;
    const z = lerp(ride.previous.z, ride.pose.z, alpha);
    const x = lerp(ride.previous.x, ride.pose.x, alpha);
    const travelled = lerp(ride.previous.travelled, ride.pose.travelled, alpha);
    const moving = ride.phase === DozerPhase.Driving || ride.pose.speed > 0;

    // Body: into the lane change (heading of the lateral speed, capped), the engine rumbling.
    const lateral = (ride.pose.x - ride.previous.x) / this.fixedStep;
    const forward = Math.max(ride.pose.speed, MIN_STEER_FORWARD);
    const maxYaw = p.steerYawDegrees * DEG_TO_RAD;
    entry.steer = damp(entry.steer, clamp(Math.atan2(lateral, forward), -maxYaw, maxYaw), p.steerYawRate, dt);
    const rumble = (moving ? p.rumble.drive : p.rumble.idle) * Math.sin(TAU * p.rumble.hz * this.time);
    const placed = ride.segment.placement({ x, y: ride.ground + rumble, z });
    const root = entry.visual.root;
    root.position.set(placed.x, placed.y, placed.z);
    root.rotation.y = placed.yaw + entry.steer;

    // Blade: resting parked, carried up while driving, lowered onto the pile – dipping when a heap hits it.
    const carrying = entry.pile !== null && entry.dumpedAt === null;
    const target = ride.phase === DozerPhase.Parked ? p.blade.parkedDegrees : carrying ? p.blade.pushDegrees : p.blade.driveDegrees;
    entry.dipLeft = Math.max(0, entry.dipLeft - dt);
    entry.lift = damp(entry.lift, target, p.blade.rate, dt);
    const lift = entry.lift - (p.blade.dipDegrees * entry.dipLeft) / p.blade.dipSeconds;
    this.pose(entry, lift * DEG_TO_RAD);

    // Running gear: wheels turn, the belts scroll with the distance driven (bottom run stands on the ground).
    for (const wheel of entry.wheels) this.rotate(wheel.rig, travelled / wheel.radius);
    if (moving) {
      const offset = -(travelled / this.config.vehicle.trackTileLength);
      for (const texture of entry.belt) texture.vOffset = offset - Math.floor(offset);
    }

    // Exhaust at the stack.
    root.computeWorldMatrix(true);
    const world = root.getWorldMatrix();
    Vector3.TransformCoordinatesToRef(entry.rest.exhaust, world, entry.exhaustOrigin);
    entry.exhaust.emitRate = dt > 0 ? (moving ? p.exhaust.driveRate : p.exhaust.idleRate) : 0;

    // Spray at the cutting edge (blade tip turned with the lift).
    if (ride === this.dozers.active) {
      this.bladeTip(entry, lift * DEG_TO_RAD, this.scratch);
      Vector3.TransformCoordinatesToRef(this.scratch, world, this.sprayOrigin);
    }
    this.updatePile(entry, dt, lift * DEG_TO_RAD);
    this.updateFill(entry);
  }

  /** Blade pivot, lift cylinders (barrel and rod aimed at each other). */
  private pose(entry: Entry, lift: number): void {
    const pivot = entry.nodes.get(DOZER_RIG.pivotBlade);
    // A positive Babylon pitch lowers what lies ahead of the pivot – lifting the blade is the negative pitch.
    if (pivot) this.rotate(pivot, -lift);
    for (const side of DOZER_SIDES) {
      const barrel = entry.nodes.get(DOZER_RIG.cylinderBarrel(side));
      const rod = entry.nodes.get(DOZER_RIG.cylinderRod(side));
      if (!barrel || !rod) continue;
      const body = entry.rest.bodyEye[side];
      const bladeRest = entry.rest.bladeEye[side];
      const blade = this.turnAboutPivot(bladeRest, entry.rest.pivot, -lift, this.scratch2);
      // Angles about x of the eye-to-eye direction (atan2(z, y): rotating +y by it about x points along it).
      const restBarrel = Math.atan2(bladeRest.z - body.z, bladeRest.y - body.y);
      const nowBarrel = Math.atan2(blade.z - body.z, blade.y - body.y);
      this.rotateInParent(barrel, nowBarrel - restBarrel);
      // The rod hangs on the pivot: its parent frame turned by the pitch already.
      const restRod = Math.atan2(body.z - bladeRest.z, body.y - bladeRest.y);
      const nowRod = Math.atan2(body.z - blade.z, body.y - blade.y);
      this.rotateInParent(rod, nowRod - restRod + lift);
    }
  }

  /** Blade tip in vehicle space for the given lift. */
  private bladeTip(entry: Entry, lift: number, out: Vector3): Vector3 {
    return this.turnAboutPivot(entry.rest.bladeTip, entry.rest.pivot, -lift, out);
  }

  /** A vehicle-space point on the blade, turned by the pivot pitch (Babylon: positive pitch lowers +z). */
  private turnAboutPivot(point: Vector3, pivot: Vector3, pitch: number, out: Vector3): Vector3 {
    const y = point.y - pivot.y;
    const z = point.z - pivot.z;
    const c = Math.cos(pitch);
    const s = Math.sin(pitch);
    out.set(point.x, pivot.y + y * c - z * s, pivot.z + y * s + z * c);
    return out;
  }

  /** Turn about the node's own x axis on top of its rest rotation (wheels, the blade pivot). */
  private rotate(rig: RigNode, pitch: number): void {
    Quaternion.RotationYawPitchRollToRef(0, pitch, 0, this.turn);
    rig.node.rotationQuaternion ??= new Quaternion();
    if (rig.rotation) rig.rotation.multiplyToRef(this.turn, rig.node.rotationQuaternion);
    else rig.node.rotationQuaternion.copyFrom(this.turn);
  }

  /** Turn about the parent's x axis applied after the rest rotation (cylinders: their rest roll may differ). */
  private rotateInParent(rig: RigNode, pitch: number): void {
    Quaternion.RotationYawPitchRollToRef(0, pitch, 0, this.turn);
    rig.node.rotationQuaternion ??= new Quaternion();
    if (rig.rotation) this.turn.multiplyToRef(rig.rotation, rig.node.rotationQuaternion);
    else rig.node.rotationQuaternion.copyFrom(this.turn);
  }

  /** The earth pile in front of the blade: grows with each heap, slides into the pit when it is filled. */
  private updatePile(entry: Entry, dt: number, lift: number): void {
    const pile = entry.pile;
    if (!pile) return;
    const p = this.config.presentation.pile;
    // ≈ 95 % of the way within growSeconds (three time constants)
    entry.pileScale = damp(entry.pileScale, entry.pileTarget, GROW_TIME_CONSTANTS / p.growSeconds, dt);
    let scale = entry.pileScale;
    let sink = 0;
    let push = 0;
    if (entry.dumpedAt !== null) {
      const u = clamp01((this.time - entry.dumpedAt) / p.dumpSeconds);
      scale *= 1 - u;
      sink = u * (entry.ride.pit?.depth ?? 0) * 0.5;
      push = u * this.heapSize.z;
    }
    pile.root.setEnabled(scale > MIN_SCALE);
    if (scale <= MIN_SCALE) return;
    const tip = this.bladeTip(entry, lift, this.scratch);
    // In front of the cutting edge, in the machine's heading (segment space).
    const root = entry.visual.root;
    const ahead = tip.z + (this.heapSize.z / 2) * scale + push;
    const yaw = root.rotation.y;
    pile.root.position.set(root.position.x + Math.sin(yaw) * ahead, root.position.y - sink, root.position.z + Math.cos(yaw) * ahead);
    pile.root.rotation.y = yaw;
    pile.root.scaling.setAll(scale);
  }

  /** The filled pit: earth rises from the pit floor to the road, a flat mound per lane on top. */
  private updateFill(entry: Entry): void {
    const pit = entry.ride.pit;
    if (entry.dumpedAt === null || !pit) return;
    const f = this.config.presentation.fill;
    const u = smoothstep(clamp01((this.time - entry.dumpedAt) / f.riseSeconds));
    const parent = this.track.rootOf(entry.ride.segment);
    if (!entry.fill) {
      const box = CreateBox('FX_DOZER_FILL', { width: pit.xTo - pit.xFrom, height: pit.depth, depth: pit.zTo - pit.zFrom }, this.scene);
      box.material = this.fillMaterial;
      box.isPickable = false;
      box.receiveShadows = true;
      box.parent = parent;
      entry.fill = box;
      for (let lane = 0; lane < this.world.laneCount; lane++) {
        const mound = this.pool.acquire(this.config.presentation.pile.assetId, parent);
        mound.root.position.set(laneCenterX(this.world, lane), this.world.groundHeight, (pit.zFrom + pit.zTo) / 2);
        entry.mounds.push(mound);
      }
    }
    const height = Math.max(MIN_SCALE, u) * pit.depth;
    entry.fill.scaling.y = height / pit.depth;
    entry.fill.position.set((pit.xFrom + pit.xTo) / 2, this.world.groundHeight - pit.depth + height / 2, (pit.zFrom + pit.zTo) / 2);
    const mound = u * (f.moundHeight / this.heapSize.y);
    for (const visual of entry.mounds) {
      visual.root.setEnabled(mound > MIN_SCALE);
      visual.root.scaling.set(this.world.laneWidth / this.heapSize.x, Math.max(MIN_SCALE, mound), (pit.zTo - pit.zFrom) / this.heapSize.z);
    }
  }

  /** Seat socket of the driven dozer in world space (the rider's hips). */
  private updateSeat(entry: Entry): void {
    const world = entry.visual.root.getWorldMatrix();
    const seat = this.config.vehicle.seat;
    const offset = this.config.presentation.rider.fallbackOffset;
    Vector3.TransformCoordinatesFromFloatsToRef(seat.x, seat.y, seat.z, world, this.seat.position);
    Vector3.TransformCoordinatesFromFloatsToRef(seat.x + offset.x, seat.y + offset.y, seat.z + offset.z, world, this.seat.fallback);
    Vector3.TransformNormalFromFloatsToRef(0, 0, 1, world, this.scratch);
    this.seat.yaw = Math.atan2(this.scratch.x, this.scratch.z);
  }

  /** Bottles springing out of a pushed heap, fanning out and flying into the rider. */
  private updateBottles(): void {
    const f = this.config.presentation.fountain;
    const target = this.seatPose()?.position;
    for (let i = this.bottles.length - 1; i >= 0; i--) {
      const bottle = this.bottles[i]!;
      const t = (this.time - bottle.start) / f.seconds;
      if (t >= 1 || !target) {
        this.pool.release(bottle.visual);
        this.bottles.splice(i, 1);
        continue;
      }
      const arc = 4 * t * (1 - t);
      const root = bottle.visual.root;
      Vector3.LerpToRef(bottle.from, target, t, root.position);
      root.position.y += f.height * arc;
      root.position.x += Math.cos(this.seat.yaw) * f.spread * bottle.fan * arc;
      root.position.z -= Math.sin(this.seat.yaw) * f.spread * bottle.fan * arc;
      root.rotation.set(t * TAU * bottle.fan, t * TAU, 0);
    }
  }

  // ── Events ───────────────────────────────────────────────────────────────

  private onHeap(ride: DozerRide, heap: ObstacleInstance): void {
    const entry = this.entries.get(ride);
    if (!entry) return;
    const p = this.config.presentation;
    entry.dipLeft = p.blade.dipSeconds;
    entry.pileTarget = Math.min(p.pile.maxScale, p.pile.firstScale + p.pile.perHeapScale * (ride.heapsPushed - 1));
    if (!entry.pile) {
      entry.pile = this.pool.acquire(p.pile.assetId, this.track.rootOf(ride.segment));
      entry.pileScale = MIN_SCALE;
    }
    this.spray.manualEmitCount = p.spray.burst;
    // Bottles out of the heap (world space – they fly to the rider whatever the track does).
    const parent = this.track.rootOf(heap.segment);
    parent.computeWorldMatrix(true);
    const placed = heap.segment.placement(heap.spawnPoint.position);
    const from = Vector3.TransformCoordinatesFromFloatsToRef(placed.x, placed.y + this.heapSize.y, placed.z, parent.getWorldMatrix(), new Vector3());
    for (let i = 0; i < p.fountain.count; i++) {
      const visual = this.pool.acquire(p.fountain.assetId, null);
      visual.root.position.copyFrom(from);
      this.bottles.push({ visual, from, start: this.time, fan: p.fountain.count > 1 ? i / (p.fountain.count - 1) - 0.5 : 0 });
    }
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  private create(ride: DozerRide): Entry | null {
    const visual = this.obstacleView.visualOf(ride.obstacle.uid);
    if (!visual) return null;
    const nodes = new Map<string, RigNode>();
    for (const node of visual.root.getChildTransformNodes(false)) {
      const name = node.name.slice(node.name.lastIndexOf(':') + 1);
      if (name.startsWith(DOZER_RIG.root)) nodes.set(name, { node, position: node.position.clone(), rotation: node.rotationQuaternion?.clone() ?? null });
    }
    const rest = this.restGeometry(visual, nodes);
    const wheels = [...nodes.entries()]
      .filter(([name]) => name.startsWith(DOZER_RIG.wheelPrefix))
      .map(([, rig]) => ({ rig, radius: wheelRadius(rig.node) }))
      .filter((wheel) => wheel.radius > 0);
    const entry: Entry = {
      ride,
      visual,
      nodes,
      wheels,
      belt: beltTextures(nodes),
      rest,
      exhaustOrigin: new Vector3(),
      exhaust: this.createExhaust(),
      lift: this.config.presentation.blade.parkedDegrees,
      dipLeft: 0,
      steer: 0,
      pile: null,
      pileScale: MIN_SCALE,
      pileTarget: 0,
      dumpedAt: null,
      fill: null,
      mounds: [],
    };
    entry.exhaust.emitter = entry.exhaustOrigin;
    this.entries.set(ride, entry);
    return entry;
  }

  /**
   * Socket and eye positions in vehicle space, taken from the visual itself (GLB nodes live in mirrored glTF space,
   * the greybox nodes in game space – world matrices resolve both alike).
   */
  private restGeometry(visual: EntityVisual, nodes: ReadonlyMap<string, RigNode>): RestGeometry {
    const root = visual.root;
    root.computeWorldMatrix(true);
    const inverse = root.getWorldMatrix().clone().invert();
    const local = (name: string, fallback: Vec3): Vector3 => {
      const node = nodes.get(name)?.node;
      if (!node) return new Vector3(fallback.x, fallback.y, fallback.z);
      forceWorld(node, root);
      return Vector3.TransformCoordinates(node.getAbsolutePosition(), inverse);
    };
    const seat = this.config.vehicle.seat;
    const eyes = (pick: (side: DozerSide) => string): Record<DozerSide, Vector3> => ({
      L: local(pick('L'), seat),
      R: local(pick('R'), seat),
    });
    return {
      pivot: local(DOZER_RIG.pivotBlade, seat),
      bladeTip: local(DOZER_RIG.bladeTip, seat),
      exhaust: local(DOZER_RIG.exhaust, seat),
      bodyEye: eyes(DOZER_RIG.cylinderBarrel),
      bladeEye: eyes(DOZER_RIG.cylinderRod),
    };
  }

  private release(entry: Entry): void {
    for (const rig of entry.nodes.values()) {
      rig.node.position.copyFrom(rig.position);
      if (rig.rotation) rig.node.rotationQuaternion = rig.rotation.clone();
      else rig.node.rotationQuaternion = null;
    }
    entry.exhaust.dispose(false);
    if (entry.pile) this.pool.release(entry.pile);
    for (const mound of entry.mounds) this.pool.release(mound);
    entry.fill?.dispose();
    this.entries.delete(entry.ride);
  }

  private dropSegment(segment: SegmentInstance): void {
    for (const entry of [...this.entries.values()]) if (entry.ride.segment === segment) this.release(entry);
  }

  private createExhaust(): ParticleSystem {
    const c = this.config.presentation.exhaust;
    const system = new ParticleSystem('FX_DOZER_EXHAUST', c.capacity, this.scene);
    system.particleTexture = this.sprite;
    system.direction1 = new Vector3(-0.15, 1, -0.3);
    system.direction2 = new Vector3(0.15, 1, -0.6);
    system.minEmitPower = c.speed.min;
    system.maxEmitPower = c.speed.max;
    system.gravity = new Vector3(0, c.rise, 0);
    system.minSize = c.size.min;
    system.maxSize = c.size.max;
    system.addSizeGradient(0, 1);
    system.addSizeGradient(1, c.size.growth);
    system.minLifeTime = c.lifetime.min;
    system.maxLifeTime = c.lifetime.max;
    system.color1 = new Color4(...c.color);
    system.color2 = new Color4(...c.color);
    system.colorDead = new Color4(...c.colorFade);
    system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    system.emitRate = 0;
    system.start();
    return system;
  }

  private createSpray(): ParticleSystem {
    const c = this.config.presentation.spray;
    const system = new ParticleSystem('FX_DOZER_SPRAY', c.capacity, this.scene);
    system.particleTexture = this.sprite;
    system.emitter = this.sprayOrigin;
    system.minEmitBox = new Vector3(-1, 0, 0);
    system.maxEmitBox = new Vector3(1, 0.4, 0.3);
    system.direction1 = new Vector3(-0.8, 1, 0.2);
    system.direction2 = new Vector3(0.8, 1.6, 1);
    system.minEmitPower = c.speed.min;
    system.maxEmitPower = c.speed.max;
    system.gravity = new Vector3(0, c.gravity, 0);
    system.minSize = c.size.min;
    system.maxSize = c.size.max;
    system.addSizeGradient(0, 1);
    system.addSizeGradient(1, c.size.growth);
    system.minLifeTime = c.lifetime.min;
    system.maxLifeTime = c.lifetime.max;
    system.minAngularSpeed = -2;
    system.maxAngularSpeed = 2;
    system.color1 = new Color4(...c.color);
    system.color2 = new Color4(...c.color);
    system.colorDead = new Color4(...c.colorFade);
    system.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    system.emitRate = 0;
    system.start();
    return system;
  }
}

/** Computes the world matrices from `root` down to `node` (parents first) – absolute positions of this frame. */
function forceWorld(node: TransformNode, root: TransformNode): void {
  const chain: TransformNode[] = [];
  for (let n: TransformNode | null = node; n && n !== root; n = n.parent as TransformNode | null) chain.push(n);
  for (let i = chain.length - 1; i >= 0; i--) chain[i]!.computeWorldMatrix(true);
}

/** Radius of a wheel: half the height of its meshes (dozerRig.ts contract), 0 if it has none. */
function wheelRadius(node: TransformNode): number {
  node.computeWorldMatrix(true);
  const meshes = node.getChildMeshes(false);
  if (node instanceof AbstractMesh) meshes.push(node);
  if (meshes.length === 0) return 0;
  const { min, max } = node.getHierarchyBoundingVectors(true);
  return Math.max(0, (max.y - min.y) / 2);
}

/** Textures of the belt material(s) – instances share their source's material, so these are shared by all copies. */
function beltTextures(nodes: ReadonlyMap<string, RigNode>): Texture[] {
  const textures = new Set<Texture>();
  for (const side of DOZER_SIDES) {
    const node = nodes.get(DOZER_RIG.track(side))?.node;
    if (!node) continue;
    const meshes = node.getChildMeshes(false);
    if (node instanceof AbstractMesh) meshes.push(node);
    for (const mesh of meshes) {
      const material: Material | null = mesh instanceof InstancedMesh ? mesh.sourceMesh.material : mesh.material;
      if (!material) continue;
      for (const slot of BELT_TEXTURE_SLOTS) {
        const texture = (material as unknown as Partial<Record<(typeof BELT_TEXTURE_SLOTS)[number], BaseTexture | null>>)[slot];
        if (texture instanceof Texture) textures.add(texture);
      }
    }
  }
  return [...textures];
}
